import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { GroupBase, OptionsOrGroups } from 'react-select';
import api from '../../../../components/api';
import { yupErrorMsg } from '../../../../components/atoms/const';

interface InitialValuesData {
  'firstname': string;
  'clientName': { value: number, label: string } | null;
  'lastname': string;
  'phoneNumber': string;
  'email': string;
  'streetAddress': string;
  'zipCode': string;
  'state': { value: string, label: string } | null;
  'city': string;
  'requestAmount': number;
  'refundReason': { value: number, label: string } | null;
  'comment': string;
  'vmId': { value: number, label: string } | null;
}

function useAddRefund() {
  // Navigation handler
  const navigation = useNavigate();
  const navigateToLanding = () => navigation('/customer-support/refund');

  /* Initiate Select Data */
  // 1. Search VM
  const [vmInputValue, setVmInputValue] = React.useState<string>('');
  // eslint-disable-next-line no-unused-vars
  const optionsFetcher = (text: string, callback: (options: OptionsOrGroups<{ value: any, label: string }, GroupBase<{ value: string, label: string }>>) => void) => {
    api.vendingMachine.findAll(text)
      .then((res) => {
        const resData = res?.data?.data;
        if (resData) {
          const options = resData.filter((x) => !!x.id && x.model !== '' && !!x.model).map((product) => ({
            value: product.id,
            label: product.model,
          }));
          callback(options);
        }
      });
  };
  // 2. Get State
  const { data: stateRes, isLoading: stateLoad } = useQuery(
    ['getAllStates', 'client'],
    () => api.clients.getAllStates(),
  );
  const stateList = React.useMemo(() => {
    const resData = stateRes?.data?.data;
    if (resData) {
      return resData.map((x) => ({
        value: x.state,
        label: x.state,
      }));
    }
    return [];
  }, [stateRes]);

  const { mutate: addRequest, isLoading: addRequestLoad } = useMutation(api.customerSupport.addRefundRequest, {
    onMutate: (variables) => console.log(variables),
    onSuccess: (data) => {
      console.log(data);
      alert('success');
      navigateToLanding();
    },
    onError: (error) => {
      console.log(error);
      alert('error');
    },
  });

  const yupSchema = yup.object({
    firstname: yup.string().required(yupErrorMsg.FIRST_NAME_EMPTY),
    lastname: yup.string().required(yupErrorMsg.LAST_NAME_EMPTY),
    phoneNumber: yup.string().required(yupErrorMsg.PHONE_NUMBER_EMPTY),
    email: yup.string().email(yupErrorMsg.EMAIL_INCORRECT_FORMAT).required(yupErrorMsg.EMAIL_EMPTY),
    streetAddress: yup.string().required(yupErrorMsg.STREET_ADDRESS_EMPTY),
    zipCode: yup.number().required(yupErrorMsg.ZIP_CODE_EMPTY),
    state: yup.object().nullable().required(yupErrorMsg.STATE_EMPTY),
    city: yup.string().required(yupErrorMsg.CITY_EMPTY),
    requestAmount: yup.number().min(0, yupErrorMsg.REQUESTED_AMOUNT_POSITIVE).required(yupErrorMsg.REQUESTED_AMOUNT_EMPTY),
    refundReason: yup.object().nullable().required(yupErrorMsg.REFUND_REASON_EMPTY),
    // 'comment': '',
    vmId: yup.object().nullable().required(yupErrorMsg.VM_EMPTY),
  });

  const initValues: InitialValuesData = {
    firstname: '',
    clientName: null,
    lastname: '',
    phoneNumber: '',
    email: '',
    streetAddress: '',
    zipCode: '',
    state: null,
    city: '',
    requestAmount: 0,
    refundReason: null,
    comment: '',
    vmId: null,
  };

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: yupSchema,
    onSubmit: (values) => {
      if (!!values.refundReason?.value && !!values.vmId?.value && !!values.state?.value) {
        addRequest({
          firstname: values.firstname,
          lastname: values.lastname,
          phoneNumber: values.phoneNumber,
          email: values.email,
          address: {
            streetAddress: values.streetAddress,
            zipCode: values.zipCode,
            state: values.state.value,
            city: values.city,
          },
          requestAmount: values.requestAmount,
          refundReason: values.refundReason.value,
          comment: '',
          vmId: values.vmId.value,
        });
      }
    },
  });

  const { data: clientRes, isLoading: clientLoad } = useQuery(
    ['getAllActive', 'clients'],
    () => api.clients.getAllActive(),
  );
  const clientList = React.useMemo(() => clientRes?.data?.data.map((x) => ({
    value: x.id,
    label: x.name,
  })), [clientRes]);

  const vmList = React.useMemo(() => {
    const resData = clientRes?.data?.data;
    if (clientList && clientList.length && !!formik.values.clientName && resData) {
      const findData = resData.find((x) => x.id === formik.values.clientName?.value);
      return findData?.vendingMachines.map((x) => ({
        value: x.id,
        label: x.deviceId,
      }));
    }
    return [];
  }, [formik.values.clientName]);

  return {
    formik,
    addRequestLoad,
    navigateToLanding,
    vm: {
      inputValue: vmInputValue,
      setInputValue: setVmInputValue,
      optionsFetcher,
      list: vmList,
    },
    client: {
      list: clientList,
      load: clientLoad,
    },
    state: {
      list: stateList,
      load: stateLoad,
    },
  };
}

export default useAddRefund;
